import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ServerErrorRed } from '@randstad-lean-mobile-factory/react-assets/dist/illustrations';
import {
  Button,
  Card,
  Checkbox,
  Loader,
  SearchInput,
} from '@randstad-lean-mobile-factory/react-components-core';

import SearchableDropDown from '../../../Components/SearchableDropDown';
import { useFetchAgenciesByBrandCode } from '../../../Hooks/OnCallCenters/useFetchAgenciesByBrandCode';
import { useFetchOnCallCenters } from '../../../Hooks/OnCallCenters/useFetchOnCallCenters';
import { OnCallCenterAgencyDto } from '../../../Services/API';
import AddToOnCallCenterModalComponent from '../../Modals/AddToOnCallCenterModal/AddToOnCallCenterModal.component';
import EditOnCallCenterModalComponent from '../../Modals/EditOnCallCenterModal/EditOnCallCenterModal.component';
import { brandCodes } from '../RoutingsSection/RoutingsSection.types';
import TopBar from '../TopBar';

import styles from './OnCallCentersSection.module.scss';

const OnCallCentersSection = () => {
  const { data: onCallCenters, isLoading, isSuccess } = useFetchOnCallCenters();
  const [brandCode, setBrandCode] = useState('307AM');
  const {
    data: agencies,
    isLoading: isLoadingAgencies,
    isSuccess: isSuccessAgencies,
  } = useFetchAgenciesByBrandCode(brandCode);

  const [globalFilter, setGlobalFilter] = useState('');
  const [isOpenAttachModal, setIsOpenAttachModal] = useState(false);

  const [unAttachedAgencies, setUnAttachedAgencies] = useState<OnCallCenterAgencyDto[]>([]);
  const [checkedAgencies, setCheckedAgencies] = useState<OnCallCenterAgencyDto[]>([]);

  useEffect(() => {
    if (isSuccessAgencies && isSuccess) {
      const onCallCentersIds = onCallCenters.map(({ onCallCenterId }) => onCallCenterId);
      const attachedAgencies = onCallCenters
        .flatMap(({ agencies }) => agencies)
        .sort((a, b) => a.agencyId.localeCompare(b.agencyId));
      setUnAttachedAgencies(
        agencies
          ?.filter(
            agency =>
              ![...attachedAgencies.map(agency => agency.agencyId), onCallCentersIds].includes(
                agency.agencyId
              )
          )
          .sort((a, b) => a.agencyId.localeCompare(b.agencyId))
      );
    }
  }, [onCallCenters, isSuccess, isSuccessAgencies, agencies]);

  const filteredUnattachedAgencies = useMemo(() => {
    return unAttachedAgencies.filter(agency =>
      agency.agencyId.toLowerCase().includes(globalFilter.toLowerCase())
    );
  }, [globalFilter, unAttachedAgencies]);

  const filteredOnCallCenters = useMemo(() => {
    return onCallCenters?.filter(
      onCallCenter =>
        onCallCenter.onCallCenterId.toLowerCase().includes(globalFilter.toLowerCase()) ||
        onCallCenter.agencies.map(agency => agency.agencyId).includes(globalFilter.toUpperCase())
    );
  }, [onCallCenters, globalFilter]);

  const handleCheckboxChange = useCallback(
    (agencyId: string) => {
      const agency = agencies?.find(agency => agency.agencyId === agencyId);
      if (!agency) return;
      if (checkedAgencies.includes(agency)) {
        setCheckedAgencies(checkedAgencies.filter(checkedAgency => checkedAgency !== agency));
      } else {
        setCheckedAgencies([...checkedAgencies, agency]);
      }
    },
    [agencies, checkedAgencies]
  );

  const isChecked = useCallback(
    (agencyId: string) => {
      const agency = agencies?.find(agency => agency.agencyId === agencyId);
      if (!agency) return;
      return checkedAgencies.includes(agency);
    },
    [agencies, checkedAgencies]
  );

  return (
    <>
      <TopBar />
      <div className={styles.content}>
        <div className={styles.header}>
          <SearchInput
            size="large"
            value={globalFilter}
            onInput={event => setGlobalFilter((event.target as HTMLInputElement).value)}
            placeholder="rechercher une unité"
          />
          <SearchableDropDown
            items={brandCodes}
            selectedItem={brandCode}
            keyValueExtractor={item => {
              return {
                key: item,
                label: item,
                value: item,
                item,
              };
            }}
            onSelectItem={item => {
              if (!item) return;
              setBrandCode(item);
            }}
            placeholder="choisir une marque"
          />
        </div>
        {isLoading || isLoadingAgencies ? (
          <Loader size="medium" variant="primary" />
        ) : isSuccess && isSuccessAgencies ? (
          <div className={styles.mainContainer}>
            <div className={styles.styledListLeft}>
              <p className={styles.title}>
                {`${filteredUnattachedAgencies.length} unité${filteredUnattachedAgencies?.length !== 1 ? 's' : ''} non rattachée${filteredUnattachedAgencies?.length !== 1 ? 's' : ''}`}
              </p>
              <div className={styles.agenciesList}>
                <Card color="beige">
                  <Checkbox
                    checked={unAttachedAgencies.length === checkedAgencies.length}
                    halfChecked={!!checkedAgencies.length}
                    onChange={() => {
                      if (unAttachedAgencies.length === checkedAgencies.length) {
                        setCheckedAgencies([]);
                      } else {
                        setCheckedAgencies(unAttachedAgencies);
                      }
                    }}
                  />
                  <Button
                    variant="primary"
                    onClick={() => {
                      setIsOpenAttachModal(true);
                    }}
                    disabled={!checkedAgencies.length}
                  >
                    rattacher à une permanence
                  </Button>
                  <AddToOnCallCenterModalComponent
                    agencies={checkedAgencies}
                    setOnCallCenterAgencies={setCheckedAgencies}
                    isOpen={isOpenAttachModal}
                    setIsOpen={setIsOpenAttachModal}
                  />
                </Card>
                {filteredUnattachedAgencies.map(agency => {
                  return (
                    <Card color="beige" key={agency.agencyId} selected={isChecked(agency.agencyId)}>
                      <Checkbox
                        checked={isChecked(agency.agencyId)}
                        onChange={() => {
                          handleCheckboxChange(agency.agencyId);
                        }}
                      />
                      {agency.agencyId}
                    </Card>
                  );
                })}
              </div>
            </div>
            <div className={styles.styledListRight}>
              <p
                className={styles.title}
              >{`${filteredOnCallCenters?.length} permanence${onCallCenters?.length > 1 ? 's' : ''}`}</p>
              {filteredOnCallCenters?.map(onCallCenter => {
                return (
                  <>
                    <Card color="beige" className={styles.cardContent}>
                      <div>
                        <p className={styles.cardHeader}>{onCallCenter.onCallCenterId}</p>
                        <p
                          className={styles.cardBody}
                        >{`${onCallCenter.agencies.length} unité${onCallCenters?.length !== 1 ? 's' : ''} : ${onCallCenter.agencies.map(agency => agency.agencyId).join(', ')}`}</p>
                      </div>
                      <EditOnCallCenterModalComponent
                        onCallCenterId={onCallCenter.onCallCenterId}
                      />
                    </Card>
                  </>
                );
              })}
            </div>
          </div>
        ) : (
          <ServerErrorRed />
        )}
      </div>
    </>
  );
};

export default OnCallCentersSection;
